import React, { useContext, useEffect } from 'react';
import { GrowthBook as GB } from '@growthbook/growthbook-react';
import { useSelector } from 'react-redux';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { getDealer } from '../../redux/dealers/selectors';

const growthBook = new GB({
  apiHost: process.env.REACT_APP_GB_API_HOST,
  clientKey: process.env.REACT_APP_GB_CLIENT_KEY,
});

export type GrowthBook = {
  getFeatureValue: (key: Feature, fallback: any) => any;
  isFeatureEnabled: (feature: Feature) => boolean;
  setAttributes: (attributes: Record<string, any>) => Promise<void>;
};

const GrowthBookContext = React.createContext<GrowthBook>({
  getFeatureValue: (_, fallback) => fallback,
  isFeatureEnabled: _ => false,
  setAttributes: async _ => undefined,
});

export enum Feature {
  TEST = 'test',
  USE_DOCUMENT_ENGINE_FOR_PREVIEW = 'use-document-engine-for-preview',
  RTA_CREDIT_FIRST_ENHANCEMENTS = 'rel-18895-rta-credit-first-enhancements',
  USE_DESKING_DATA_FROM_SERVER = 'rel-20066-use-desking-data-on-server',
  HIDE_FINANCE_OFFERS_TAB = 'rel-20394-hide-finance-offers-tab-in-deskingv2',
  SUBARU_RESERVATION_ENABLED = 'rel-22716-subaru-forester-hybrid-reservation',
  DESKING_V3 = 'rel-18371-desking-v3',
  SHOW_RIC_VALIDATIONS_IN_CC = 'arlcredit-1312-show-ric-validation-errors-in-cc',
}

export const GrowthBookProvider = ({
  children,
}: {
  children?: React.ReactNode;
}): React.ReactElement => {
  const dealer: Dealer = useSelector(getDealer);

  useEffect(() => {
    growthBook.init({ streaming: true });
  }, []);

  useEffect(() => {
    if (dealer) {
      setAttributes({
        dealerId: dealer.id,
      });
    }
  }, [dealer]);

  const getFeatureValue = (key: Feature, fallback: any): any =>
    growthBook.isOn(key) ? growthBook.getFeatureValue(key, fallback) : fallback;

  const isFeatureEnabled = (feature: Feature): boolean =>
    growthBook.isOn(feature);

  const setAttributes = async (
    attributes: Record<string, any>,
  ): Promise<void> =>
    growthBook.setAttributes({
      ...growthBook.getAttributes(),
      ...attributes,
    });

  return React.createElement(
    GrowthBookContext.Provider,
    { value: { getFeatureValue, isFeatureEnabled, setAttributes } },
    children,
  );
};

export const useGrowthBook = (): GrowthBook => useContext(GrowthBookContext);
